/**
 * **Pure closed-form dice probability** — no `DiceSource`, no randomness, no
 * engine imports. Every die in this codebase is fair and independent
 * (`packages/rules/src/dice.ts`), so every quantity here is an exact combinatorial
 * computation, not a Monte-Carlo approximation — validated against brute-force enumeration
 * of the underlying dice space in `probability.test.ts`.
 *
 * Mirrors two mechanics from `packages/rules/src/dice.ts`:
 *   - **accuracy/difficulty** (`roll()`): `|acc|` d6 are rolled and only the SINGLE HIGHEST is
 *     kept, so net accuracy `n` isn't a flat `+n` — it's a random `+X` where `X` is the max of
 *     `|n|` d6 (magnitude), signed by whether `n` is accuracy (+) or difficulty (−).
 *   - **crit** (`roll({crit:true})`): roll DOUBLE the dice, keep the top half. **Overkill**
 *     (`rollOverkill`) rerolls each 1 until it lands non-1 (so its post-reroll face is uniform
 *     over `{2,…,sides}`, one fewer face than the die's own range) and tallies 1 heat per reroll,
 *     drawn over the FULL pool (crit's doubled pool too, `resolve/damage.ts`'s `rollInstanceDice`) —
 *     *then* crit's keep-highest selection runs on the post-reroll values.
 */

/** `C(n, k)`, the binomial coefficient — small-integer-safe (weapon dice pools never exceed ~single digits). */
function binomialCoefficient(n: number, k: number): number {
  if (k < 0 || k > n) return 0;
  let result = 1;
  const kk = Math.min(k, n - k);
  for (let i = 0; i < kk; i++) {
    result = (result * (n - i)) / (i + 1);
  }
  return result;
}

/** `P(Binomial(n, p) = k)`. */
function binomialPmf(n: number, k: number, p: number): number {
  if (p <= 0) return k === 0 ? 1 : 0;
  if (p >= 1) return k === n ? 1 : 0;
  return binomialCoefficient(n, k) * p ** k * (1 - p) ** (n - k);
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * The exact PMF of the **magnitude** die an accuracy/difficulty roll of `k` (`k = |net accuracy|
 * >= 1`) draws — the highest of `k` d6 (`roll()`'s `keepHighest(draws, 1)`). Returns `P(X=x)` for
 * `x` in `1..6`, index 0 unused.
 */
function accuracyMagnitudePmf(k: number): number[] {
  const pmf = new Array<number>(7).fill(0);
  for (let x = 1; x <= 6; x++) {
    pmf[x] = (x ** k - (x - 1) ** k) / 6 ** k;
  }
  return pmf;
}

export type HitCritArgs = {
  /** The attacker's flat attack bonus (`weapon.attackBonus ?? attacker.base.attackBonus ?? 0`). */
  bonus: number;
  /** Net accuracy/difficulty (positive = accuracy, negative = difficulty, 0 = plain d20) — the SAME signed net `rollD20`'s `acc` param takes. */
  netAcc: number;
  /** The target's defense number (Evasion, or E-DEFENSE for a tech/Smart attack). */
  defense: number;
  /** `attacker.base.noCrits` — Veteran/Ultra NPC's RAW crit exception; forces `pCrit` to 0. */
  noCrits?: boolean;
};

/**
 * Exact `P(hit)`/`P(crit)` for `d20 + bonus + accuracyMod >= defense` (hit) and `... >= max(defense, 20)`
 * (crit — `resolve.ts`: "total >= 20 AND a hit", not a natural 20). Sums the plain-d20 hit/crit
 * band over the accuracy magnitude die's PMF when `netAcc !== 0`.
 */
export function hitCritProbabilities(args: HitCritArgs): { pHit: number; pCrit: number } {
  const { bonus, netAcc, defense, noCrits } = args;
  const bandFraction = (threshold: number): number => clamp(21 - threshold, 0, 20) / 20;
  const pHitGivenMod = (mod: number): number => bandFraction(defense - bonus - mod);
  const pCritGivenMod = (mod: number): number => bandFraction(Math.max(defense, 20) - bonus - mod);

  const k = Math.abs(netAcc);
  if (k === 0) {
    return { pHit: pHitGivenMod(0), pCrit: noCrits ? 0 : pCritGivenMod(0) };
  }
  const sign = netAcc > 0 ? 1 : -1;
  const pmf = accuracyMagnitudePmf(k);
  let pHit = 0;
  let pCrit = 0;
  for (let x = 1; x <= 6; x++) {
    const mod = sign * x;
    pHit += pmf[x]! * pHitGivenMod(mod);
    pCrit += pmf[x]! * pCritGivenMod(mod);
  }
  return { pHit, pCrit: noCrits ? 0 : pCrit };
}

type DistKey = string;

/**
 * The exact probability distribution of the **sum of the top `n` values among `m` i.i.d. dice**,
 * each uniform over the integer face range `[loFace, hiFace]`. `m === n` (the non-crit case) sums
 * every die (order doesn't matter). `m === 2n` (the crit case, `keepHighest`) is a genuine
 * order-statistic sum — computed exactly via sequential face-by-face conditioning: among `r` dice
 * known to be `<= v`, the count landing exactly on `v` is `Binomial(r, 1/(v-loFace+1))` (each
 * remaining die is uniform over `{loFace,…,v}` by construction), so the whole distribution
 * decomposes into a small memoized recursion over `(dice remaining, selection slots remaining,
 * current face)` — no sampling, no approximation.
 */
export type TopNDiceSumArgs = {
  /** The total dice pool rolled (`n` for a plain sum, `2n` for a crit's keep-highest). */
  m: number;
  /** How many of the `m` dice are kept (the top `n`, by value). */
  n: number;
  /** The lowest face a die can show (1 normally, 2 for an Overkill-rerolled die). */
  loFace: number;
  /** The highest face a die can show (the weapon's `sides`). */
  hiFace: number;
};

export function topNDiceSumDistribution(args: TopNDiceSumArgs): Map<number, number> {
  const { m, n, loFace, hiFace } = args;
  if (m <= 0 || n <= 0) return new Map([[0, 1]]);
  const memo = new Map<DistKey, Map<number, number>>();

  function f(r: number, s: number, v: number): Map<number, number> {
    if (s <= 0 || r <= 0 || v < loFace) return new Map([[0, 1]]);
    const key: DistKey = `${r}|${s}|${v}`;
    const cached = memo.get(key);
    if (cached) return cached;
    const facesRemaining = v - loFace + 1;
    const p = 1 / facesRemaining;
    const out = new Map<number, number>();
    for (let k = 0; k <= r; k++) {
      const pk = binomialPmf(r, k, p);
      if (pk === 0) continue;
      const contribution = Math.min(k, s) * v;
      const sub = f(r - k, Math.max(0, s - k), v - 1);
      for (const [subSum, subP] of sub) {
        const total = contribution + subSum;
        out.set(total, (out.get(total) ?? 0) + pk * subP);
      }
    }
    memo.set(key, out);
    return out;
  }

  return f(m, n, hiFace);
}

/** `E[X]` over a `{value: probability}` distribution (e.g. {@link topNDiceSumDistribution}'s output). */
export function expectedValue(dist: Map<number, number>): number {
  let total = 0;
  for (const [v, p] of dist) total += v * p;
  return total;
}

/**
 * The exact expected **heat** an Overkill weapon generates for its wielder — `1` per reroll,
 * summed over the FULL rolled pool (crit's doubled pool included, drawn BEFORE the keep-highest
 * selection — `resolve/damage.ts`'s `rollInstanceDice`). Each die's reroll count is geometric with
 * per-attempt miss probability `1/sides` (landing on `1`): `E[rerolls] = (1/sides)/(1-1/sides) =
 * 1/(sides-1)`.
 */
export function expectedOverkillHeat(pool: number, sides: number): number {
  if (pool <= 0 || sides <= 1) return 0;
  return pool / (sides - 1);
}

/** The dice pool + face range for a damage roll — the shared shape {@link topNDiceSumDistribution} needs, derived from a weapon's Overkill/crit state. */
export function damageDicePool(count: number, isCrit: boolean, overkill: boolean): { pool: number; loFace: number } {
  return { pool: isCrit ? count * 2 : count, loFace: overkill ? 2 : 1 };
}
